import { Component } from '@opten/gin-tonic/helpers/compiler';
import { gsap } from 'gsap';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

@Component({
	className: 'js-scroll-animations',
})
export class ScrollAnimations {
	// use GSAP's responsive helper so animations only run on desktop
	private mm = gsap.matchMedia();

	// keep references we can clean up on breakpoint changes
	private smoother: ScrollSmoother | null = null;
	private desktopTick?: () => void;

	// Clear GSAP-managed inline styles for desktop-only animated elements
	private clearDesktopGsapStyles(root: HTMLElement) {
		// Select only the elements this class animates on desktop. Intentionally
		// exclude scroll gallery elements which also run on mobile.
		const selectors = [
			// generic section headings
			'.c-section__title',
			'.c-section__subtitle',
			// teaser
			'.js-teaser .c-teaser',
			'.js-teaser .c-teaser__surtitle',
			'.js-teaser .c-teaser__title',
			'.js-teaser .c-teaser__lead',
			'.js-teaser .c-teaser__text',
			'.js-teaser .c-teaser__media',
			'.js-teaser .c-teaser .c-btn',
			// icon links
			'.js-icon-links',
			'.js-icon-links__item .c-icon-link',
			'.js-icon-links .c-btn',
			// team/tiles
			'.js-team .c-team-card',
			'.js-tiles .c-tile-card',
			// partner slider
			'.js-gsap-partner-slider .c-section__title',
			// keyvisual
			'.js-gsap-keyvisual-section .c-keyvisual__slide',
			'.js-gsap-keyvisual-section .c-keyvisual__bg',
			// accordion
			'.js-gsap-accordion .c-accordion__title',
			'.js-gsap-accordion .c-accordion-item',
			// text-image
			'.js-gsap-text-image .c-text-image__media',
			'.js-gsap-text-image .c-text-image__content',
			'.js-gsap-text-image .c-text-image__description',
			// texts
			'.js-gsap-texts .c-texts__content',
			// testimonial
			'.js-gsap-testimonial .c-testimonial__inner',
			// image
			'.js-gsap-image .c-image__media',
			'.js-gsap-image .c-image__description',
			// blog teaser
			'.js-gsap-blog-teaser .c-blog-card',
			'.js-gsap-blog-teaser .c-blog-teaser__actions .c-btn',
			// screen partners
			'.js-gsap-screen-partners .c-screen-partners__group-title',
			'.js-gsap-screen-partners .c-screen-partners__logos-title',
			'.js-gsap-screen-partners .c-screen-partners__logos',
		];

		const targets = Array.from(
			root.querySelectorAll<HTMLElement>(selectors.join(','))
		);

		targets.forEach((el) => {
			// Skip clearing for elements inside a no-anim wrapper since
			// some animations (like teaser-first) are allowed on mobile.
			if ((el as HTMLElement).closest('.js-no-gsap-anim')) return;
			// stop any running tweens
			gsap.killTweensOf(el);
			// remove the common GSAP-set properties without nuking legitimate inline styles
			gsap.set(el, {
				clearProps:
					'x,y,opacity,scale,rotate,transform,filter,clipPath,height,width,marginTop,marginBottom,boxShadow,willChange',
			});
		});

		// Kill ScrollTriggers that belong to our desktop-only pieces (exclude scroll galleries)
		ScrollTrigger.getAll().forEach((st) => {
			const trg = (st as any).trigger ?? (st as any).vars?.trigger;
			const contained = (node: any) =>
				node instanceof Element &&
				root.contains(node) &&
				!(node as HTMLElement).closest('.js-scroll-gallery') &&
				// also preserve triggers inside the no-anim wrapper, because
				// some are allowed to run on mobile (e.g., teaser-first)
				!(node as HTMLElement).closest('.js-no-gsap-anim');
			if (trg instanceof Element) {
				if (contained(trg)) st.kill();
			} else if (trg && typeof (trg as any).forEach === 'function') {
				let shouldKill = false;
				(trg as any).forEach((n: any) => {
					if (contained(n)) shouldKill = true;
				});
				if (shouldKill) st.kill();
			}
		});
	}

	// Helper to skip animations for elements inside a no-anim wrapper
	private shouldSkip(elem: Element): boolean {
		return !!(elem as HTMLElement).closest('.js-no-gsap-anim');
	}

	constructor(private element: HTMLElement) {
		gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

		// Only enable ScrollSmoother and all scroll animations on desktop
		// Mobile/tablet (below 1200px) will not initialize any GSAP effects.
		// Always run scroll gallery animations (desktop and mobile)
		const scrollGallery =
			this.element.querySelectorAll<HTMLElement>('.js-scroll-gallery');
		scrollGallery.forEach((elem) => {
			if (this.shouldSkip(elem)) return;
			this.animateScrollGallery(elem);
		});

		// --- Add window resize handler to reload scroll galleries ---
		// Debounced: update scroll galleries and refresh ScrollTrigger measurements
		const handleResize = () => {
			// Rebuild scroll galleries (kill + recreate their ScrollTriggers)
			scrollGallery.forEach((elem) => {
				if (this.shouldSkip(elem)) return;
				// Remove previous ScrollTrigger(s) for this element
				ScrollTrigger.getAll().forEach((st) => {
					if (st.trigger === elem) st.kill();
				});
				this.animateScrollGallery(elem);
			});
		};
		window.addEventListener('resize', handleResize);

		// Always run teaser-first for teasers that would otherwise be skipped
		// (inside .js-no-gsap-anim). This should run on all breakpoints,
		// similar to the scroll gallery behavior.
		const teasersAll = this.element.querySelectorAll<HTMLElement>('.js-teaser');
		teasersAll.forEach((elem) => {
			if (this.shouldSkip(elem)) {
				this.animateTeaserFirst(elem);
			}
		});

		// All other animations only on desktop
		this.mm.add('(min-width: 1200px)', () => {
			// Create smoother and forward its frame updates as window 'scroll'
			// so components that rely on scroll events (like ScrollGalleryComponent)
			// also update during smoothing inertia/decay frames.
			this.smoother = ScrollSmoother.create({
				smooth: 4,
				effects: true,
				content: this.element,
				// TS typings for ScrollSmoother may not declare onUpdate; cast via any.
			} as any);

			// If the plugin supports onUpdate, wire it; otherwise fallback to ticker.
			try {
				// @ts-ignore - optional at runtime
				if (typeof (this.smoother as any).vars === 'object') {
					// Recreate with onUpdate if available on this build
					(this.smoother as any).vars.onUpdate = () =>
						window.dispatchEvent(new Event('scroll'));
				}
			} catch {}

			// Always forward frames using GSAP's ticker to be safe.
			const tick = () => {
				window.dispatchEvent(new Event('scroll'));
			};
			gsap.ticker.add(tick);
			this.desktopTick = tick;

			// Unified section header animations
			const headers =
				this.element.querySelectorAll<HTMLElement>('.c-section__header');
			headers.forEach((header) => {
				if (this.shouldSkip(header)) return;
				// Skip headers managed by module-specific logic
				this.animateSectionHeader(header);
			});

			const zoomInUps =
				this.element.querySelectorAll<HTMLElement>('.js-icon-links');
			zoomInUps.forEach((elem) => {
				if (this.shouldSkip(elem)) return;
				this.animateIconLinks(elem);
			});

			const teaser = this.element.querySelectorAll<HTMLElement>('.js-teaser');
			teaser.forEach((elem) => {
				// Always run the standard teaser animation on desktop.
				// Teaser-first (for skipped elements) is initialized outside
				// the desktop-only block to run on all breakpoints.
				this.animateTeaser(elem);
			});

			const team = this.element.querySelectorAll<HTMLElement>('.js-team');
			team.forEach((elem) => {
				if (this.shouldSkip(elem)) return;
				this.animateTeam(elem);
			});

			const tiles = this.element.querySelectorAll<HTMLElement>('.js-tiles');
			tiles.forEach((elem) => {
				if (this.shouldSkip(elem)) return;
				this.animateTiles(elem);
			});

			const partnerSliders = this.element.querySelectorAll<HTMLElement>(
				'.js-gsap-partner-slider'
			);
			partnerSliders.forEach((elem) => {
				if (this.shouldSkip(elem)) return;
				this.animatePartnerSlider(elem);
			});

			const keyvisualSections = this.element.querySelectorAll<HTMLElement>(
				'.js-gsap-keyvisual-section'
			);
			keyvisualSections.forEach((elem) => {
				if (this.shouldSkip(elem)) return;
				this.animateKeyvisualSection(elem);
			});

			const accordions =
				this.element.querySelectorAll<HTMLElement>('.js-gsap-accordion');
			accordions.forEach((elem) => {
				if (this.shouldSkip(elem)) return;
				this.animateAccordion(elem);
			});

			const textImages = this.element.querySelectorAll<HTMLElement>(
				'.js-gsap-text-image'
			);
			textImages.forEach((elem) => {
				if (this.shouldSkip(elem)) return;
				this.animateTextImage(elem);
			});

			const texts =
				this.element.querySelectorAll<HTMLElement>('.js-gsap-texts');
			texts.forEach((elem) => {
				if (this.shouldSkip(elem)) return;
				this.animateTexts(elem);
			});

			const testimonials = this.element.querySelectorAll<HTMLElement>(
				'.js-gsap-testimonial'
			);
			testimonials.forEach((elem) => {
				if (this.shouldSkip(elem)) return;
				this.animateTestimonial(elem);
			});

			const images =
				this.element.querySelectorAll<HTMLElement>('.js-gsap-image');
			images.forEach((elem) => {
				if (this.shouldSkip(elem)) return;
				this.animateImage(elem);
			});

			const blogTeasers = this.element.querySelectorAll<HTMLElement>(
				'.js-gsap-blog-teaser'
			);
			blogTeasers.forEach((elem) => {
				if (this.shouldSkip(elem)) return;
				this.animateBlogTeaser(elem);
			});

			const screenPartners = this.element.querySelectorAll<HTMLElement>(
				'.js-gsap-screen-partners'
			);
			screenPartners.forEach((elem) => {
				if (this.shouldSkip(elem)) return;
				this.animateScreenPartners(elem);
			});

			// cleanup when breakpoint no longer matches
			return () => {
				if (this.desktopTick) {
					gsap.ticker.remove(this.desktopTick);
					this.desktopTick = undefined;
				}
				try {
					this.smoother?.kill();
					this.smoother = null;
				} catch {}
				// Remove desktop-only ScrollTriggers and inline props so mobile renders clean
				this.clearDesktopGsapStyles(this.element);
				// Do NOT remove the scroll gallery resize listener – mobile still uses it.
			};
		});

		// Hash links take priority; otherwise try to restore the scroll
		// position the user left off at (reload / back-forward only).
		if (location.hash) {
			this.scrollToHashTarget();
		} else {
			this.restoreScrollPosition();
		}
		this.bindScrollPersistence();
	}

	// New: unified animation for section headers (title + subtitle)
	private animateSectionHeader(header: HTMLElement): void {
		const title = header.querySelector<HTMLElement>('.c-section__title');
		const subtitle = header.querySelector<HTMLElement>('.c-section__subtitle');
		const skipOut = header.closest('.js-no-header-out');
		const skipIn = header.closest('.js-no-header-in');

		if (!title && !subtitle) return;

		const isCentered = header.classList.contains('c-section__header--center');

		if (isCentered) {
			if (!skipIn) {
				// Fade up in, fade up/out when reaching top (like team module style)
				if (title) gsap.set(title, { y: 20, opacity: 0, scale: 0.8 });
				if (subtitle) gsap.set(subtitle, { y: 20, opacity: 0, scale: 0.8 });
			}

			const tlIn = gsap.timeline({
				scrollTrigger: {
					trigger: header,
					start: 'top-=100px bottom',
					end: 'center-=100px center',
					scrub: true,
				},
			});

			if (!skipIn) {
				if (title) tlIn.to(title, { y: 0, opacity: 1, scale: 1 });
				if (subtitle) tlIn.to(subtitle, { y: 0, opacity: 1, scale: 1 }, '<0.2');
			}

			const tlOut = gsap.timeline({
				scrollTrigger: {
					trigger: header,
					start: 'bottom-=250px top',
					end: 'bottom-=50px top',
					scrub: true,
				},
			});
			if (!skipOut) {
				if (title) tlOut.to(title, { y: -40, opacity: 0, scale: 0.95 }, 0);
				if (subtitle)
					tlOut.to(subtitle, { y: -40, opacity: 0, scale: 0.95 }, 0);
			}
		} else {
			if (!skipIn) {
				// Slide in from left, slide/fade out to the opposite direction (right)
				if (title) gsap.set(title, { x: '-50%', opacity: 0, scale: 0.8 });
				if (subtitle) gsap.set(subtitle, { x: '-50%', opacity: 0, scale: 0.8 });
			}

			const tlIn = gsap.timeline({
				scrollTrigger: {
					trigger: header,
					start: 'top bottom',
					end: 'top bottom-=150px',
					scrub: true,
				},
			});
			if (!skipIn) {
				if (title) tlIn.to(title, { x: 0, opacity: 1, scale: 1 });
				if (subtitle) tlIn.to(subtitle, { x: 0, opacity: 1, scale: 1 }, '<0.2');
			}

			const tlOut = gsap.timeline({
				scrollTrigger: {
					trigger: header,
					start: 'bottom-=150px top',
					end: 'bottom top',
					scrub: true,
				},
			});
			if (!skipOut) {
				if (title) tlOut.to(title, { x: '-50%', opacity: 0, scale: 0.95 }, 0);
				if (subtitle)
					tlOut.to(subtitle, { x: '-50%', opacity: 0, scale: 0.95 }, 0);
			}
		}
	}

	animateScreenPartners(elem: HTMLElement): void {
		const items = elem.querySelectorAll<HTMLElement>(
			'.c-screen-partners__item'
		);

		items.forEach((item, index) => {
			const logoGroup = item.querySelector<HTMLElement>(
				'.c-screen-partners__logos'
			);
			const isReversed =
				item?.querySelector('.c-screen-partners__content.reversed') !== null;

			const tlItem = gsap.timeline({
				scrollTrigger: {
					trigger: item,
					start: 'center bottom',
					end: 'bottom bottom',
					scrub: true,
				},
			});

			const groupTitle = item.querySelector('.c-screen-partners__logos-title');

			if (groupTitle) {
				gsap.set(groupTitle, { x: isReversed ? '-100%' : '100%' });
				tlItem.to(groupTitle, { x: 0, opacity: 1, scale: 1 }, index * 0.15);
			}

			const groups = item.querySelectorAll<HTMLElement>(
				'.c-screen-partners__group'
			);
			groups.forEach((group, groupIndex) => {
				const logosTitle = group.querySelector<HTMLElement>(
					'.c-screen-partners__group-title'
				);
				if (logosTitle) {
					gsap.set(logosTitle, { x: isReversed ? '-100%' : '100%' });
					tlItem.to(
						logosTitle,
						{ x: 0, opacity: 1, scale: 1 },
						(index + groupIndex) * 0.15 + 0.1
					);
				}

				const logoGroup = group.querySelector<HTMLElement>(
					'.c-screen-partners__logos'
				);
				const logos =
					logoGroup?.querySelectorAll<HTMLElement>(
						'.c-screen-partners__logo'
					) ?? [];
				logos.forEach((logo, logoIndex) => {
					gsap.set(logo, { x: isReversed ? 500 : -500, opacity: 0 });
					tlItem.to(
						logo,
						{ x: 0, opacity: 1 },
						(index + groupIndex + logoIndex) * 0.12 + 0.2
					);
				});
			});
		});
	}

	animateTexts(elem: HTMLElement): void {
		const header = elem.querySelector<HTMLElement>('.c-section__header');
		const title =
			header?.querySelector<HTMLElement>('.c-section__title') ?? null;
		const subtitle =
			header?.querySelector<HTMLElement>('.c-section__subtitle') ?? null;
		const isCentered = header?.classList.contains('c-section__header--center');
		const content = elem.querySelectorAll<HTMLElement>('.c-texts__content');

		// Custom header animation for texts (avoid early exit due to pinning)
		if (header) {
			if (isCentered) {
				if (title) gsap.set(title, { y: 20, opacity: 0, scale: 0.9 });
				if (subtitle) gsap.set(subtitle, { y: 20, opacity: 0, scale: 0.9 });
				const tlIn = gsap.timeline({
					scrollTrigger: {
						trigger: elem,
						start: `top+=${header.offsetHeight + 100}px bottom`,
						end: `top+=${header.offsetHeight + 250}px bottom`,
						scrub: true,
					},
				});
				if (title) tlIn.to(title, { y: 0, opacity: 1 });
				if (subtitle) tlIn.to(subtitle, { y: 0, opacity: 1 }, '<0.2');

				const tlOut = gsap.timeline({
					scrollTrigger: {
						trigger: elem,
						start: 'bottom-=350px top',
						end: 'bottom-=100px top',
						scrub: true,
					},
				});
				if (title) tlOut.to(title, { y: -40, opacity: 0 }, 0);
				if (subtitle) tlOut.to(subtitle, { y: -40, opacity: 0 }, 0);
			} else {
				// Left-to-right in, right-to-left out, but timed to the section lifecycle
				if (title) gsap.set(title, { x: '-50%', opacity: 0, scale: 0.95 });
				if (subtitle)
					gsap.set(subtitle, { x: '-50%', opacity: 0, scale: 0.95 });
				const tlIn = gsap.timeline({
					scrollTrigger: {
						trigger: elem,
						start: `top+=${header.offsetHeight + 50}px bottom`,
						end: `top+=${header.offsetHeight + 200}px bottom`,
						scrub: true,
					},
				});
				if (title) tlIn.to(title, { x: 0, opacity: 1 });
				if (subtitle) tlIn.to(subtitle, { x: 0, opacity: 1 }, '<0.2');

				const tlOut = gsap.timeline({
					scrollTrigger: {
						trigger: elem,
						start: 'bottom-=100px top',
						end: 'bottom top',
						scrub: true,
					},
				});
				if (title) tlOut.to(title, { x: '-50%', opacity: 0 }, 0);
				if (subtitle) tlOut.to(subtitle, { x: '-50%', opacity: 0 }, 0);
			}
		}

		// Content entrance
		content.forEach((contentElem) => {
			gsap.set(contentElem, { scale: 0.9, opacity: 0, y: 10 });
		});
		const tlContent = gsap.timeline({
			scrollTrigger: {
				trigger: content,
				start: 'top bottom-=50px',
				end: 'top bottom-=150px',
				scrub: true,
			},
		});

		content.forEach((contentElem, index) => {
			tlContent.to(contentElem, { scale: 0.9, opacity: 1, y: 0 }, '+=0.15');
		});

		// Pin for scroll interaction (no fade-in here)
		gsap.timeline({
			scrollTrigger: {
				trigger: elem,
				start: `center center`,
				end: `center center-=25%`,
				scrub: true,
				pin: true,
			},
		});

		// Zoom effect while scrolling (not from the beginning)
		content.forEach((contentElem) => {
			gsap
				.timeline({
					scrollTrigger: {
						trigger: elem,
						start: 'center center',
						end: 'bottom center',
						scrub: true,
					},
				})
				.to(contentElem, { scale: 1.0, ease: 'none' });
		});

		// Apply the same zoom to the header title/subtitle so scaling matches content
		if (header) {
			if (title) {
				gsap
					.timeline({
						scrollTrigger: {
							trigger: elem,
							start: 'center center',
							end: 'bottom center',
							scrub: true,
						},
					})
					.to(title, { scale: 1.0, ease: 'none' });
			}
			if (subtitle) {
				gsap
					.timeline({
						scrollTrigger: {
							trigger: elem,
							start: 'center center',
							end: 'bottom center',
							scrub: true,
						},
					})
					.to(subtitle, { scale: 1.0, ease: 'none' });
			}
		}

		// Out animation for content only
		const tlOut = gsap.timeline({
			scrollTrigger: {
				trigger: elem,
				start: `bottom-=350px top`,
				end: `bottom-=100px top`,
				scrub: true,
			},
		});
		content.forEach((contentElem, index) => {
			tlOut.to(contentElem, { y: -40, opacity: 0 }, `<${index * 0.1}`);
		});
	}

	animateTextImage(elem: HTMLElement): void {
		const isInverted = elem.querySelector('.c-text-image--invert') !== null;
		const image = elem.querySelector<HTMLElement>('.c-text-image__media');
		const text = elem.querySelector<HTMLElement>('.c-text-image__content');

		// Image timeline (unchanged)
		const tl = gsap.timeline({
			scrollTrigger: {
				trigger: elem,
				start: `top bottom`,
				end: `center center`,
				scrub: true,
			},
		});

		gsap.set(image, { x: !isInverted ? '-100%' : '100%' });
		tl.to(image, { x: 0 }, '0+=0.2');

		const description = elem.querySelector<HTMLElement>(
			'.c-text-image__description'
		);
		if (description) {
			gsap.set(description, { x: !isInverted ? '-100%' : '100%', opacity: 0 });

			gsap
				.timeline({
					scrollTrigger: {
						trigger: description,
						start: 'top bottom',
						end: 'top bottom-=150px',
						scrub: true,
					},
				})
				.to(description, { x: 0, opacity: 1 });

			gsap
				.timeline({
					scrollTrigger: {
						trigger: description,
						start: 'bottom-=150px top',
						end: 'bottom top',
						scrub: true,
					},
				})
				.to(
					description,
					{ x: !isInverted ? '-100%' : '100%', opacity: 0 },
					0
				);
		}

		if (text) {
			gsap.set(text, { x: '-50%', opacity: 0, scale: 0.8 });

			const tlTextIn = gsap.timeline({
				scrollTrigger: {
					trigger: text,
					start: 'top bottom',
					end: 'top bottom-=150px',
					scrub: true,
				},
			});
			tlTextIn.to(text, { x: 0, opacity: 1, scale: 1 });

			const tlTextOut = gsap.timeline({
				scrollTrigger: {
					trigger: text,
					start: 'bottom-=150px top',
					end: 'bottom top',
					scrub: true,
				},
			});
			tlTextOut.to(text, { x: '-50%', opacity: 0, scale: 0.95 }, 0);
		}

		const tlOut = gsap.timeline({
			scrollTrigger: {
				trigger: elem,
				start: `bottom-=450px top`,
				end: `bottom-=200px top`,
				scrub: true,
			},
		});
		tlOut.to(image, { y: -50, opacity: 0 }, 0);
	}

	animateTestimonial(elem: HTMLElement): void {
		const card = elem.querySelector<HTMLElement>('.c-testimonial__inner');
		if (!card) return;

		gsap.set(card, { y: 100, opacity: 0, scale: 0.8 });

		gsap
			.timeline({
				scrollTrigger: {
					trigger: elem,
					start: 'top bottom',
					end: 'center center',
					scrub: true,
				},
			})
			.to(card, { y: 0, opacity: 1, scale: 1 });

		gsap
			.timeline({
				scrollTrigger: {
					trigger: card,
					start: 'center top+=175px',
					end: 'bottom top+=175px',
					scrub: true,
				},
			})
			.to(card, { y: -100, opacity: 0, scale: 0.8 });
	}

	animateImage(elem: HTMLElement): void {
		const media = elem.querySelector<HTMLElement>('.c-image__media');
		const description = elem.querySelector<HTMLElement>(
			'.c-image__description'
		);

		if (media) {
			gsap.set(media, { y: 60, opacity: 0, scale: 0.95 });
			gsap
				.timeline({
					scrollTrigger: {
						trigger: elem,
						start: 'top bottom',
						end: 'center center',
						scrub: true,
					},
				})
				.to(media, { y: 0, opacity: 1, scale: 1 });

			gsap
				.timeline({
					scrollTrigger: {
						trigger: elem,
						start: 'bottom-=450px top',
						end: 'bottom-=200px top',
						scrub: true,
					},
				})
				.to(media, { y: -50, opacity: 0 }, 0);
		}

		if (description) {
			gsap.set(description, { x: '-100%', opacity: 0 });
			gsap
				.timeline({
					scrollTrigger: {
						trigger: description,
						start: 'top bottom',
						end: 'top bottom-=150px',
						scrub: true,
					},
				})
				.to(description, { x: 0, opacity: 1 });

			gsap
				.timeline({
					scrollTrigger: {
						trigger: description,
						start: 'bottom-=150px top',
						end: 'bottom top',
						scrub: true,
					},
				})
				.to(description, { x: '-100%', opacity: 0 }, 0);
		}
	}

	animateBlogTeaser(elem: HTMLElement): void {
		const cards = elem.querySelectorAll<HTMLElement>('.c-blog-card');
		const grid = elem.querySelector<HTMLElement>('.c-blog-teaser__grid');
		const button = elem.querySelector<HTMLElement>(
			'.c-blog-teaser__actions .c-btn'
		);

		const tl = gsap.timeline({
			scrollTrigger: {
				trigger: elem,
				start: `top bottom`,
				end: `center center`,
				scrub: true,
			},
		});

		const tlOut = gsap.timeline({
			scrollTrigger: {
				trigger: grid,
				start: `center top+=175px`,
				end: `bottom top+=175px`,
				scrub: true,
			},
		});

		cards.forEach((card, index) => {
			gsap.set(card, { y: 100, opacity: 0, scale: 0.8 });
			tl.to(
				card,
				{ y: 0, opacity: 1, scale: 1 },
				`<0.2+=${(index + 1) / cards.length}`
			);
			tlOut.to(
				card,
				{ y: -100, opacity: 0, scale: 0.8 },
				`<0.2+=${(index + 1) / cards.length}`
			);
		});

		if (button) {
			gsap.set(button, { y: 20, opacity: 0, scale: 0.9 });
			gsap
				.timeline({
					scrollTrigger: {
						trigger: button,
						start: 'top bottom',
						end: 'top bottom-=150px',
						scrub: true,
					},
				})
				.to(button, { y: 0, opacity: 1, scale: 1 });

			gsap
				.timeline({
					scrollTrigger: {
						trigger: button,
						start: 'bottom-=150px top',
						end: 'bottom top',
						scrub: true,
					},
				})
				.to(button, { y: -20, opacity: 0, scale: 0.9 });
		}
	}

	animateAccordion(elem: HTMLElement): void {
		const items = elem.querySelectorAll<HTMLElement>('.c-accordion-item');
		const title = elem.querySelector<HTMLElement>('.c-accordion__title');

		// Section title: keep a single timeline for the whole accordion header/section title
		if (title) {
			gsap.set(title, { x: '-50%', opacity: 0 });
			const tlTitleIn = gsap.timeline({
				scrollTrigger: {
					trigger: title,
					start: 'top bottom',
					end: 'top bottom-=150px',
					scrub: true,
				},
			});
			tlTitleIn.to(title, { x: 0, opacity: 1 }, 0);

			const tlTitleOut = gsap.timeline({
				scrollTrigger: {
					trigger: title,
					start: 'bottom-=150px top',
					end: 'bottom top',
					scrub: true,
				},
			});
			tlTitleOut.to(title, { x: '-50%', opacity: 0 }, 0);
		}

		// Per-item in/out timelines: trigger is always the item itself
		items.forEach((item) => {
			// Initial state for item (off to the right and invisible)
			gsap.set(item, { x: 500, opacity: 0 });

			// IN animation for this item:
			// - start when the item's top reaches the viewport bottom
			// - end when the item's bottom reaches the viewport bottom
			gsap
				.timeline({
					scrollTrigger: {
						trigger: item,
						start: 'top bottom',
						end: 'bottom+=20px bottom',
						scrub: true,
						invalidateOnRefresh: true,
					},
				})
				.to(item, { x: 0, opacity: 1, ease: 'power2.inOut' });

			// OUT animation for this item:
			// - start when the item's top reaches the viewport top + 100px
			// - end when the item's bottom reaches the viewport top
			gsap
				.timeline({
					scrollTrigger: {
						trigger: item,
						start: 'top-=120px top',
						end: 'bottom-=100px top',
						scrub: true,
						invalidateOnRefresh: true,
					},
				})
				.to(item, { x: 200, opacity: 0, ease: 'power2.inOut' });
		});
	}

	animateKeyvisualSection(elem: HTMLElement): void {
		const items = elem.querySelectorAll<HTMLElement>('.c-keyvisual__slide');
		items.forEach((item) => {
			const image = item.querySelector<HTMLElement>('.c-keyvisual__bg');
			const tl = gsap.timeline({
				scrollTrigger: {
					trigger: item,
					start: `top bottom`,
					end: `bottom top`,
					scrub: true,
				},
			});
			gsap.set(image, { y: `${(-1 * window.innerHeight) / 4}px` });
			tl.to(image, { y: `${window.innerHeight / 4}px` });
		});
	}

	animatePartnerSlider(elem: HTMLElement): void {
		const tl = gsap.timeline({
			scrollTrigger: {
				trigger: elem,
				start: `top bottom`,
				end: `center center`,
				scrub: true,
			},
		});
		// Header handled globally
	}

	animateIconLinks(elem: HTMLElement): void {
		const cards = elem.querySelectorAll<HTMLElement>('.js-icon-links__item');
		const button = elem.querySelector<HTMLElement>('.c-btn');
		const tl = gsap.timeline({
			scrollTrigger: {
				trigger: elem,
				start: `top bottom`,
				end: `center center`,
				scrub: true,
			},
		});

		const tlOut = gsap.timeline({
			scrollTrigger: {
				trigger: elem,
				start: `bottom-=500px top`,
				end: `bottom-=300px top`,
				scrub: true,
			},
		});

		const tlOutBtn = gsap.timeline({
			scrollTrigger: {
				trigger: elem,
				start: `bottom-=300px top`,
				end: `bottom-=100px top`,
				scrub: true,
			},
		});

		cards.forEach((card, index) => {
			const tmp = card.querySelector('.c-icon-link');
			gsap.set(tmp, { y: 100, opacity: 0, scale: 0.8 });
			tl.to(
				tmp,
				{ y: 0, opacity: 1, scale: 1 },
				`0+=${(index + 1) / cards.length}`
			);
			tlOut.to(
				tmp,
				{ y: -100, opacity: 0, scale: 0.8 },
				`<0.2+=${(index + 1) / cards.length}`
			);
		});

		gsap.set(button, { y: 20, opacity: 0, scale: 0.9 });
		tl.to(button, { y: 0, opacity: 1, scale: 1 });
		tlOutBtn.to(button, { y: -20, opacity: 0, scale: 0.9 });
	}

	animateTeaser(elem: HTMLElement): void {
		const surtitle = elem.querySelector<HTMLElement>('.c-teaser__surtitle');
		const title = elem.querySelector<HTMLElement>('.c-teaser__title');
		const lead = elem.querySelector<HTMLElement>('.c-teaser__lead');
		const text = elem.querySelector<HTMLElement>('.c-teaser__text');
		const buttons = elem.querySelectorAll<HTMLElement>('.c-btn');
		const img = elem.querySelector<HTMLElement>('.c-teaser__media');

		// Helper: per-part fade-up in and out triggers
		const attachFadeUp = (target: HTMLElement | null | undefined) => {
			if (!target) return;
			gsap.set(target, {
				y: 20,
				opacity: 0,
				scale: 0.95,
				willChange: 'transform',
			});
			// In
			gsap
				.timeline({
					scrollTrigger: {
						trigger: target,
						start: 'top bottom',
						end: 'top bottom-=120px',
						scrub: true,
					},
				})
				.to(target, { y: 0, opacity: 1, scale: 1, ease: 'none' });
			// Out
			gsap
				.timeline({
					scrollTrigger: {
						trigger: target,
						start: 'bottom-=250px top',
						end: 'bottom-=50px top',
						scrub: true,
					},
				})
				.to(target, { y: -20, opacity: 0, scale: 0.95, ease: 'none' });
		};

		// Attach per-element animations
		attachFadeUp(surtitle);
		attachFadeUp(title);
		attachFadeUp(lead);
		attachFadeUp(text);
		attachFadeUp(img);
		buttons.forEach((btn) => attachFadeUp(btn));
	}

	/**
	 * Special intro when the first module on the page is a teaser:
	 * Simplified version (no fancy animation):
	 * - Pin only the title from the very beginning of the section
	 * - No move-from-center or staged fade-ins; content is visible immediately
	 * - Unpin when the section ends
	 */
	animateTeaserFirst(elem: HTMLElement): void {
		// Ensure teaser-first titles are re-centered whenever ScrollTrigger
		// performs a refresh (this includes window resizes and programmatic
		// refresh calls). The per-timeline `onRefresh` was only firing once
		// for the original case; adding a global listener ensures every
		// refresh recalculates the start Y for visible teaser titles.
		try {
			// Use addEventListener to catch all refreshes. Cast to any in case
			// typings for addEventListener are not present in this env.
			(ScrollTrigger as any).addEventListener?.('refresh', () => {
				if (window.innerWidth < 1200) return; // only on desktop
				elem
					.querySelectorAll<HTMLElement>('.js-teaser .c-section__title')
					.forEach((title) => {
						try {
							const r = title.getBoundingClientRect();
							const startY = window.innerHeight / 2 - (r.top + r.height / 2);
							gsap.set(title, { y: startY });
						} catch (e) {
							// defensive: ignore individual measurement failures
						}
					});
			});
		} catch (e) {
			// defensive: fail silently if ScrollTrigger doesn't support addEventListener
		}

		const title = elem.querySelector<HTMLElement>('.c-section__title');
		const fadeTarget =
			elem.querySelector<HTMLElement>('.c-teaser-wrapper') ??
			elem.querySelector<HTMLElement>('.c-teaser');

		// Ensure the title is above other content and visible immediately
		if (title) {
			gsap.set(title, {
				position: 'relative',
				zIndex: 10,
				opacity: 1,
				willChange: 'transform',
			});
		}

		// Build a scrubbed timeline for: (1) move title from center to its natural spot, (2) slide teaser box up from bottom (no opacity changes)
		const tl = gsap.timeline({
			scrollTrigger: {
				trigger: elem,
				start: 'top top+=30px',
				end: 'bottom top+=30px',
				scrub: true,
				// Pin via the timeline's ScrollTrigger so pinning happens in sync
				// with the title animation (avoids layout shifts that caused a jump).
				pin: true,
				// Ensure measurements are recalculated on refresh
				invalidateOnRefresh: true,
				// After ScrollTrigger does its measurements and applies the pin spacer,
				// compute and set the title's starting Y so it visually starts centered.
				onRefresh: () => {
					try {
						if (title) {
							const r = title.getBoundingClientRect();
							const startY = window.innerHeight / 2 - (r.top + r.height / 2);
							// Apply the computed start position so the title appears centered
							// relative to the viewport after pin insertion.
							gsap.set(title, { y: startY });
						}

						// Place teaser box below the viewport initially so it slides in from the bottom
						if (fadeTarget) {
							gsap.set(fadeTarget, {
								y: window.innerHeight,
								willChange: 'transform',
							});
						}
					} catch (e) {
						// defensive: ignore if measurements fail
					}
				},
			},
		});

		if (title) {
			// Animate title to its natural spot (y:0). We rely on the onRefresh above
			// to set the correct starting Y so the animation starts from the visual
			// center of the viewport.
			tl.to(title, {
				y: 0,
				ease: 'none',
				duration: 0.5,
			});
		}

		// Phase 2: slide the teaser content up from the bottom (no opacity changes)
		if (fadeTarget) {
			// Start the teaser slide at the same time the title animation begins
			// so the content slides in while the title moves upward.
			tl.to(fadeTarget, { y: 0, ease: 'none', duration: 0.5 }, 0);
		}

		// NOTE: pinning is handled by the timeline ScrollTrigger above to ensure
		// the title movement and the pin happen in sync (prevents the fast jump).
		document.addEventListener('resize', () => {
			ScrollTrigger.refresh();
		});
	}

	// Pins/height changes set up by the animations above only settle after
	// images/layout finish loading, so anything that depends on final
	// measurements (hash-target scrolling, scroll-position restore) needs to
	// wait for that to happen before it can resolve a target and scroll to it.
	private afterLayoutSettled(run: () => void): void {
		const runAfterSettle = () =>
			requestAnimationFrame(() => requestAnimationFrame(run));

		if (document.readyState === 'complete') {
			runAfterSettle();
		} else {
			window.addEventListener('load', runAfterSettle, { once: true });
		}

		// Late-loading content (lazy images, resizing iframes, ...) can still
		// shift the layout after the first attempt, so re-check once more.
		window.setTimeout(run, 600);
	}

	// Native #hash scrolling lands on the wrong spot because ScrollSmoother
	// decouples window.scrollY from the actual (transformed) content position.
	// So we resolve the target manually once everything has settled and
	// scroll the smoother (or window) to it.
	private scrollToHashTarget(): void {
		const hash = location.hash?.slice(1);
		if (!hash) return;

		const target = document.getElementById(hash);
		if (!target) return;

		this.afterLayoutSettled(() => {
			ScrollTrigger.refresh();

			const headerHeight = parseFloat(
				getComputedStyle(document.documentElement).getPropertyValue(
					'--header-height'
				)
			);
			const headerOffset = Number.isFinite(headerHeight) ? headerHeight : 0;

			const currentScroll = this.smoother
				? this.smoother.scrollTop()
				: window.scrollY;
			const destination =
				currentScroll + target.getBoundingClientRect().top - headerOffset;

			if (this.smoother) {
				this.smoother.scrollTo(Math.max(0, destination), false);
			} else {
				window.scrollTo({ top: Math.max(0, destination), behavior: 'auto' });
			}
		});
	}

	// Key scroll positions per URL (path + query) so filtered/paginated views
	// (e.g. the blog overview's AJAX filters) don't clash with each other.
	private getScrollStorageKey(): string {
		return `scrollPos:${location.pathname}${location.search}`;
	}

	private getCurrentScrollTop(): number {
		return this.smoother ? this.smoother.scrollTop() : window.scrollY;
	}

	private setScrollTop(pos: number, smooth = false): void {
		if (this.smoother) {
			this.smoother.scrollTo(pos, smooth);
		} else if (smooth) {
			const reducedMotion = window.matchMedia(
				'(prefers-reduced-motion: reduce)'
			).matches;
			window.scrollTo({
				top: pos,
				behavior: reducedMotion ? 'auto' : 'smooth',
			});
		} else {
			window.scrollTo(0, pos);
		}
	}

	private saveScrollPosition(): void {
		try {
			sessionStorage.setItem(
				this.getScrollStorageKey(),
				String(this.getCurrentScrollTop())
			);
		} catch {
			// sessionStorage can be unavailable (private browsing, quota, ...)
		}
	}

	// Persist the scroll position whenever the page might go away, so a
	// reload or browser back/forward can restore it afterwards.
	private bindScrollPersistence(): void {
		window.addEventListener('pagehide', () => this.saveScrollPosition());
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'hidden') this.saveScrollPosition();
		});
	}

	// Browsers normally restore scroll position on reload/back-forward via
	// `history.scrollRestoration`, but that fires before ScrollSmoother/
	// ScrollTrigger have set up their pins and transforms, causing a visible
	// jump - which is why it's disabled in favor of this manual restore (see
	// history.scrollRestoration = 'manual' in main.ts). Plain navigations
	// (clicking a link) intentionally start at the top, matching native
	// browser behavior, so we only restore for reload/back-forward.
	private restoreScrollPosition(): void {
		const navEntry = performance.getEntriesByType('navigation')[0] as
			| PerformanceNavigationTiming
			| undefined;
		if (navEntry?.type !== 'reload' && navEntry?.type !== 'back_forward') {
			return;
		}

		let stored: string | null = null;
		try {
			stored = sessionStorage.getItem(this.getScrollStorageKey());
		} catch {
			return;
		}
		if (stored === null) return;

		const pos = parseFloat(stored);
		if (!Number.isFinite(pos) || pos <= 0) return;

		// Apply right away (as soon as the smoother/ScrollTrigger exist)
		// instead of waiting for full page load - waiting would mean the
		// page visibly sits at the top for a moment and then jumps, which
		// is far more noticeable than restoring early and possibly being a
		// few pixels off.
		ScrollTrigger.refresh();
		this.setScrollTop(pos);

		// Late-loading content or ScrollTrigger's own refresh can nudge the
		// scroll away from that target once layout finally settles. Correct
		// for that with a smooth (not instant) scroll so any residual drift
		// reads as a gentle adjustment rather than a jump.
		this.afterLayoutSettled(() => {
			ScrollTrigger.refresh();
			if (Math.abs(this.getCurrentScrollTop() - pos) > 2) {
				this.setScrollTop(pos, true);
			}
		});
	}

	animateTeam(elem: HTMLElement): void {
		const members = elem.querySelectorAll<HTMLElement>('.c-team-card');
		const team = elem.querySelector<HTMLElement>('.c-team');

		const tl = gsap.timeline({
			scrollTrigger: {
				trigger: elem,
				start: `top bottom`,
				end: `center center`,
				scrub: true,
			},
		});

		const tlOut = gsap.timeline({
			scrollTrigger: {
				trigger: team,
				start: `center top+=175px`,
				end: `bottom top+=175px`,
				scrub: true,
			},
		});

		members.forEach((member, index) => {
			gsap.set(member, { y: 100, opacity: 0, scale: 0.8 });
			tl.to(
				member,
				{ y: 0, opacity: 1, scale: 1 },
				`<0.2+=${(index + 1) / members.length}`
			);
			tlOut.to(
				member,
				{ y: -100, opacity: 0, scale: 0.8 },
				`<0.2+=${(index + 1) / members.length}`
			);
		});
	}

	animateTiles(elem: HTMLElement): void {
		const tiles = elem.querySelectorAll<HTMLElement>('.c-tile-card');
		const container = elem.querySelector<HTMLElement>('.c-tile');

		const tl = gsap.timeline({
			scrollTrigger: {
				trigger: elem,
				start: `top bottom`,
				end: `center center`,
				scrub: true,
			},
		});

		const tlOut = gsap.timeline({
			scrollTrigger: {
				trigger: container,
				start: `center top+=175px`,
				end: `bottom top+=175px`,
				scrub: true,
			},
		});

		tiles.forEach((tile, index) => {
			gsap.set(tile, { y: 100, opacity: 0, scale: 0.8 });
			tl.to(
				tile,
				{ y: 0, opacity: 1, scale: 1 },
				`<0.2+=${(index + 1) / tiles.length}`
			);
			tlOut.to(
				tile,
				{ y: -100, opacity: 0, scale: 0.8 },
				`<0.2+=${(index + 1) / tiles.length}`
			);
		});
	}

	animateScrollGallery(elem: HTMLElement): void {
		const viewport = elem.querySelector<HTMLElement>(
			'.c-scroll-gallery__viewport'
		);
		const rows = Array.from(
			elem.querySelectorAll<HTMLElement>('.js-scroll-gallery-row')
		);

		// Helper: ensure a row has enough items to cover the viewport width
		function ensureRowCoversViewport(row: HTMLElement) {
			const vpW = viewport?.clientWidth ?? window.innerWidth;
			const getRowW = () => row.scrollWidth;
			let items = Array.from(row.children) as HTMLElement[];
			if (getRowW() >= vpW || !items.length) return;
			let i = 0;
			while (getRowW() < vpW + 200 && i < items.length * 10) {
				row.appendChild(items[i % items.length].cloneNode(true));
				i++;
				items = Array.from(row.children) as HTMLElement[];
			}
		}

		const getViewportWidth = () => viewport?.clientWidth ?? window.innerWidth;
		const getExtraWidth = (row: HTMLElement) =>
			Math.max(0, row.scrollWidth - getViewportWidth());

		// Ensure coverage before measuring
		rows.forEach(ensureRowCoversViewport);

		const maxExtra = rows.reduce(
			(acc, row) => Math.max(acc, getExtraWidth(row)),
			0
		);

		// If nothing to scroll, skip creating pin/animation
		if (maxExtra <= 0) return;

		// Derive a dynamic speed so the scroll effort (pin distance) feels similar
		// across desktop sizes, while being perpetual and capped by min/max.
		// Contract:
		// - Inputs: viewport width/height, row widths
		// - Output: speed (content px per 1px scroll) clamped to [MIN_SPEED, MAX_SPEED]
		// - Target: about N screenfuls of scrolling to traverse the widest row
		const getDynamicSpeed = () => {
			const vpW = getViewportWidth();
			const vpH = window.innerHeight || 800;
			// Tune how many screen heights you want users to scroll through the gallery
			const targetScreens = window.innerWidth >= 1200 ? 2.1 : 2.5;
			const targetScrollPx = targetScreens * vpH;

			const extras = rows.map((r) => Math.max(0, r.scrollWidth - vpW));
			const maxE = Math.max(...extras);
			if (maxE <= 0) return 1; // defensive default

			// Ideal speed to achieve approximately targetScrollPx pin distance
			const ideal = maxE / targetScrollPx;
			// Prevent too fast or too slow
			const MIN_SPEED = 0.5;
			const MAX_SPEED = 1.5;
			return gsap.utils.clamp(MIN_SPEED, MAX_SPEED, ideal);
		};

		const computePinDistance = () => {
			const vpW = getViewportWidth();
			const extras = rows.map((r) => Math.max(0, r.scrollWidth - vpW));
			const maxE = Math.max(...extras);
			const speed = getDynamicSpeed();
			return Math.ceil(maxE / speed); // scroll px needed to traverse extra at dynamic speed
		};

		const master = gsap.timeline({
			scrollTrigger: {
				trigger: elem,
				start: 'top top',
				end: () => `+=${computePinDistance()}`,
				pin: true,
				scrub: true,
				invalidateOnRefresh: true,
				onRefreshInit: () => {
					// Re-ensure rows cover the viewport before re-measuring
					rows.forEach(ensureRowCoversViewport);
				},
			},
		});

		rows.forEach((row) => {
			const direction = (
				row.getAttribute('data-direction') || 'left'
			).toLowerCase();
			const extra = getExtraWidth(row);
			// For 'right' rows, start offset so images overflow left; for 'left', overflow right
			const initialX = direction === 'right' ? -extra : 0;
			const finalX = direction === 'right' ? 0 : -extra;
			gsap.set(row, { x: initialX });
			master.to(
				row,
				{
					x: finalX,
					ease: 'none',
					invalidateOnRefresh: true,
				},
				0
			);
			// If images lazy-load, refresh to recompute distances
			row.querySelectorAll('img').forEach((img) => {
				if ((img as HTMLImageElement).complete) return;
				img.addEventListener('load', () => ScrollTrigger.refresh(), {
					once: true,
				});
			});
		});
	}
}
